import {
  Container,
  Graphics,
  Sprite,
  Text,
} from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { SlotSymbolDef } from './types';
import { waitForNextVideoFrame } from './waitForNextVideoFrame';

/**
 * One rendered slot symbol — a tinted rounded square containing either a
 * Sprite (when the symbol declares a {@link SlotSymbolDef#textureAlias} and
 * the asset is loaded) or an emoji glyph fallback.
 *
 * <p>v0 themes use emoji glyphs throughout; AI-generated theme bundles in
 * W6 will publish sprite atlases and set {@code textureAlias} on each
 * symbol. The sprite-or-glyph branch lives here so themes can mix-and-match
 * (e.g. the wild stays an emoji while every regular symbol is a sprite).
 */
export class SlotSymbol extends Container {
  private readonly bg: Graphics;
  private readonly glyphText: Text;
  private readonly sprite: Sprite;
  /** Video playback sprite for the celebration path (Veo-generated
   * transparent WebM, see {@link SlotSymbolDef#winVideoAlias}).
   * Renders the shared {@link AssetLoader#getVideo} texture; the
   * underlying {@code HTMLVideoElement} is owned by the loader so
   * multiple SlotSymbols winning the same spin all draw from one
   * playback head — the celebrations stay synchronised across the
   * grid. Null until the symbol's first video-driven win, then kept
   * around so we don't re-allocate per spin. */
  private winVideoSprite: Sprite | null = null;
  public def: SlotSymbolDef;

  constructor(def: SlotSymbolDef, public readonly size: number) {
    super();
    this.def = def;

    this.bg = new Graphics();
    this.addChild(this.bg);

    this.glyphText = new Text({
      text: def.glyph,
      style: {
        fontFamily:
          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", system-ui, sans-serif',
        fontSize: Math.floor(size * 0.6),
        align: 'center',
      },
    });
    this.glyphText.anchor.set(0.5);
    this.addChild(this.glyphText);

    this.sprite = new Sprite();
    this.sprite.anchor.set(0.5);
    // Fill ~92% of the cell — the commissioned art has its own padding
    // baked in (see asset brief §10.0), so going edge-to-edge is fine.
    // Earlier 0.72 left visible negative space around each symbol that
    // the (now-removed) tinted cell used to fill.
    this.sprite.width = Math.floor(size * 0.92);
    this.sprite.height = Math.floor(size * 0.92);
    this.sprite.visible = false;
    this.addChild(this.sprite);

    this.refreshFace();
    this.redrawBackground();
  }

  /** Stop any in-progress or held celebration video and revert to
   * the static face. Idempotent. Called by {@link SlotReel#spin}
   * at every spin start so the previous round's win imagery never
   * lingers across a fresh spin — even if the player presses SPIN
   * mid-celebration before the video naturally ends.
   *
   * <p>Pauses + rewinds the shared video element on the way out so
   * the next celebration starts on frame 0 cleanly. The element is
   * shared across every cell that references the same
   * {@link SlotSymbolDef#winVideoAlias}; pausing it once stops
   * every dependent sprite at the same time, which is the desired
   * grid-wide reset behaviour. */
  resetCelebration(): void {
    if (this.def.winVideoAlias) {
      const video = AssetLoader.shared.getVideo(this.def.winVideoAlias);
      if (video && !video.element.paused) {
        video.element.pause();
        video.element.currentTime = 0;
      }
    }
    this.refreshFace();
  }

  setDef(def: SlotSymbolDef): void {
    // Celebration end leaves a held trophy frame visible (see
    // playWinVideo onEnded). When the reel scrolls a new symbol
    // into this cell, force a refresh even if the id hasn't
    // changed so the held frame doesn't leak across spins.
    const heldCelebration = this.winVideoSprite?.visible === true;
    if (this.def.id === def.id && !heldCelebration) {
      return;
    }
    this.def = def;
    this.refreshFace();
    this.redrawBackground();
  }

  /** Decide between sprite and emoji based on whether the symbol's texture
   * alias resolves through the asset cache.
   *
   * <p>Also clears any held celebration imagery (the video sprite's
   * trophy last-frame). This is the natural reset point: called from
   * {@link setDef} when the reel scrolls a new symbol into this cell,
   * so the new face replaces any leftover trophy pose. */
  private refreshFace(): void {
    if (this.winVideoSprite) this.winVideoSprite.visible = false;
    const texture = this.def.textureAlias
      ? AssetLoader.shared.getTexture(this.def.textureAlias)
      : null;
    if (texture) {
      this.sprite.texture = texture;
      this.sprite.visible = true;
      this.glyphText.visible = false;
    } else {
      this.sprite.visible = false;
      this.glyphText.visible = true;
      this.glyphText.text = this.def.glyph;
    }
  }

  /**
   * Play this symbol's win-celebration animation once. Resolves when
   * the video reaches its end frame (which the engine then holds as
   * the trophy pose until the reel scrolls a new symbol into the
   * cell). Resolves immediately when the symbol has no
   * {@code winVideoAlias} or the video hasn't loaded — caller falls
   * back to the scale-pulse highlight in
   * {@link SlotReel#flashCellAtRow} via
   * {@link SlotReel#celebrateCellAtRow}.
   */
  async playWinAnimation(): Promise<void> {
    if (!this.def.winVideoAlias) return;
    const video = AssetLoader.shared.getVideo(this.def.winVideoAlias);
    if (!video) return;
    return this.playWinVideo(video);
  }

  /**
   * Play the Veo-generated celebration video as the win animation.
   * Resolves on {@code video.ended}; falls back to refreshFace() so
   * the static sprite reappears for the next idle state.
   *
   * <p>The {@link HTMLVideoElement} is owned by
   * {@link AssetLoader} and shared across every SlotSymbol that
   * references the same alias. Concurrent winning cells therefore
   * share one playback head — the celebration is grid-synchronised
   * rather than staggered (acceptable v1 behaviour; per-cell
   * playback would need per-cell video elements which is expensive).
   *
   * <p>Concurrency: when multiple cells call this in the same spin,
   * the first call resets {@code currentTime} to 0 and starts
   * playback; subsequent calls detect "playing already" and just
   * attach an {@code ended} listener, so the video doesn't restart
   * mid-celebration.
   */
  private async playWinVideo(loaded: { texture: import('pixi.js').Texture; element: HTMLVideoElement }): Promise<void> {
    // Lazy-build the video sprite. Footprint matches the static
    // sprite (size × 0.92) so the celebration sits in the same cell
    // area without re-flowing layout. Sizing is deferred to after
    // the first painted frame below — the video texture's orig dim
    // is still the 1×1 placeholder until the element loads, and
    // setting width/height on a 1×1 texture bakes scale = ~size
    // into the sprite, which then renders enormous once real frame
    // dimensions propagate.
    if (!this.winVideoSprite) {
      this.winVideoSprite = new Sprite(loaded.texture);
      this.winVideoSprite.anchor.set(0.5);
      this.winVideoSprite.visible = false;
      this.addChild(this.winVideoSprite);
    } else {
      this.winVideoSprite.texture = loaded.texture;
      this.winVideoSprite.visible = false;
    }

    const element = loaded.element;
    // Only the first cell to call (per spin-cycle) resets the
    // playhead and triggers play. Subsequent cells attach an `ended`
    // listener and let the existing playback finish — restarting
    // mid-celebration would jolt every other cell already showing
    // the awake-fox frames.
    if (element.paused || element.ended) {
      element.currentTime = 0;
      // Apply per-symbol playback rate before play() so the entire
      // arc (including the seek + decode warm-up that the rVFC
      // wait below covers) honours the configured pacing. The
      // element is shared across cells; setting playbackRate before
      // each celebration is safe because every cell of the same
      // symbol id specifies the same speed.
      element.playbackRate = this.def.winVideoSpeed ?? 1.0;
      // play() returns a Promise on most browsers — swallow rejection
      // (autoplay policy), the listener still fires `ended` when the
      // browser eventually plays the cached frames.
      void element.play().catch(() => undefined);
    }

    // Hold the static sprite as the placeholder until the video has
    // actually painted its next frame. The browser leaves the
    // <video> surface empty (black) during the seek-to-0 + decode
    // warm-up window after `currentTime = 0` and `play()`; if we
    // expose the video sprite during that window the empty surface
    // flashes through as black where the static fox used to be.
    // requestVideoFrameCallback fires once per painted frame and is
    // the right primitive — `playing` / `canplay` events fire before
    // the first frame composites. Falls back to `playing` on browsers
    // that don't ship rVFC.
    await waitForNextVideoFrame(element);

    // Size only after the first painted frame — before that the
    // video texture's orig dim is the 1×1 placeholder Pixi builds
    // for an unloaded element, and Sprite.width = w would bake in
    // scale = w (~symbol-cell size). Read element.videoWidth (not
    // texture.orig.width) to also cover the non-rVFC fallback where
    // `playing` can fire before VideoSource has propagated dims to
    // the texture. Re-applied every play so the cached sprite from
    // a prior celebration can't carry a stale scale forward.
    const w = Math.floor(this.size * 0.92);
    const intrinsic =
      element.videoWidth || this.winVideoSprite.texture.orig.width || 1;
    this.winVideoSprite.scale.set(w / intrinsic);

    this.winVideoSprite.visible = true;
    this.sprite.visible = false;
    this.glyphText.visible = false;

    return new Promise<void>((resolve) => {
      const onEnded = (): void => {
        element.removeEventListener('ended', onEnded);
        // Swap back to the static sprite when the video ends. Win
        // videos are authored idle → celebrate → idle (the start
        // and end frames match the static slot symbol PNG), so
        // hiding the video sprite and revealing the static is a
        // visually-invisible transition.
        //
        // Why not hold the video's last frame as the "trophy" pose:
        // Pixi's video texture loses its alpha channel once the
        // browser pauses playback at {@code ended}, so the held
        // frame renders as opaque-black around the character (instead
        // of transparent over the cell backdrop). Refreshing the
        // static sprite sidesteps that — the static PNG carries its
        // alpha cleanly and matches the video's idle end-frame.
        if (this.winVideoSprite) this.winVideoSprite.visible = false;
        this.refreshFace();
        resolve();
      };
      element.addEventListener('ended', onEnded, { once: true });
      // If the video was already past `ended` (cached run finished
      // between getVideo() and addEventListener), short-circuit so
      // we don't deadlock on a listener that'll never fire.
      if (element.ended) onEnded();
    });
  }

  private redrawBackground(): void {
    this.bg.clear();
    // When a themed sprite is DECLARED, render the symbol art directly
    // on the scene background — no tinted cell, no stroke. The
    // commissioned illustrations read cleanly against the theme's
    // environment art.
    //
    // Note: we check the static {@code textureAlias} on the symbol
    // def, NOT {@code this.sprite.visible}. Textures load
    // asynchronously through AssetLoader; at SlotSymbol construction
    // time the texture cache may not have the entry yet (the load
    // resolves a tick later). Using the static declaration sidesteps
    // the race — if the theme says this symbol has a sprite, trust
    // it and skip the tinted backdrop unconditionally. Emoji-glyph
    // themes (default/midnight/sunset) leave textureAlias undefined
    // and keep the tinted backdrop for legibility.
    if (this.def.textureAlias) return;
    const radius = Math.max(8, Math.floor(this.size * 0.1));
    this.bg
      .roundRect(-this.size / 2, -this.size / 2, this.size, this.size, radius)
      .fill({ color: this.def.tint, alpha: 0.92 })
      .stroke({ color: 0xffffff, alpha: 0.18, width: 2 });
  }
}

